/**
 * A DOOH post-flight campaign, sized to exercise pagination.
 *
 * ── Why this fixture is synthetic, and what it is built from ──
 *
 * DOOH has not launched (D36), so there is no history to draw on: `dooh_impressions`
 * holds **0 rows in production** and 188 genuine rows in `prodata_dev` from the 2023
 * test. X-1 warns those 188 are uniform — one multiplier (1.34) across 29 screens and
 * only 4 venue types — so they cannot exercise varying multipliers, a realistic venue
 * spread, or anything resembling scale.
 *
 * So the *shape* is real and the *volume* is synthetic. The venue-type distribution
 * below is the actual one from `prodata_dev.dooh_screens` (30,536 screens), read on
 * 16 August 2026:
 *
 *   Convenience Stores 11,223 · Casual Dining Restaurants 7,957 · Bars 3,433
 *   Retail 2,549 · Office Buildings 1,558 · Doctors Offices - General 1,210
 *   Gyms 673 · Salons 404 · Hospitals 384 · Sports Arenas 360
 *   Airports - General 162 · Hotels 128
 *
 * Multipliers vary per venue type, which the real fixture does not — a bar screen
 * reaches more people per play than a doctor's waiting room. That variation is the
 * point: a uniform multiplier would let a defect that ignores the multiplier entirely
 * still produce plausible totals.
 */

import { doohVenueTotals } from './dooh-fixture-math.js';

/** The real distribution, as weights. */
const VENUE_TYPES: Array<{ type: string; weight: number; multiplier: number }> = [
  { type: 'Convenience Stores', weight: 11223, multiplier: 1.2 },
  { type: 'Casual Dining Restaurants', weight: 7957, multiplier: 2.4 },
  { type: 'Bars', weight: 3433, multiplier: 3.1 },
  { type: 'Retail', weight: 2549, multiplier: 1.8 },
  { type: 'Office Buildings', weight: 1558, multiplier: 2.0 },
  { type: 'Doctors Offices - General', weight: 1210, multiplier: 1.34 },
  { type: 'Gyms', weight: 673, multiplier: 1.6 },
  { type: 'Salons', weight: 404, multiplier: 1.1 },
  { type: 'Hospitals', weight: 384, multiplier: 2.7 },
  { type: 'Sports Arenas', weight: 360, multiplier: 8.5 },
  { type: 'Airports - General', weight: 162, multiplier: 6.2 },
  { type: 'Hotels', weight: 128, multiplier: 1.9 },
];

const CITIES = [
  'Boston, MA 02108', 'Providence, RI 02903', 'Worcester, MA 01608',
  'Springfield, MA 01103', 'Hartford, CT 06103', 'Portland, ME 04101',
  'Manchester, NH 03101', 'New Haven, CT 06510', 'Fall River, MA 02720',
  'Nashua, NH 03060',
];

/**
 * Deterministic venue list. No RNG — the same `count` always yields the same rows, so
 * a rendered DOOH report is byte-reproducible (R4).
 */
export function buildDoohVenues(count: number) {
  const venues = [];
  for (let i = 0; i < count; i++) {
    const vt = VENUE_TYPES[i % VENUE_TYPES.length];
    // Plays vary 1..40 in a fixed cycle, so totals are not a flat multiple of count.
    const plays = 1 + ((i * 7) % 40);
    venues.push({
      screenId: String(400000 + i),
      venueType: vt.type,
      venueName: `${vt.type.split(' ')[0]} #${1000 + i}`,
      cityStateZip: CITIES[i % CITIES.length],
      plays,
      impressions: Number((plays * vt.multiplier).toFixed(2)),
      // One screen in every 500 is deliberately unmatched, so the report exercises
      // D39's "visible sync gap" path rather than always taking the happy route.
      unmatchedScreen: i % 500 === 499,
    });
  }
  return venues;
}

export function buildDoohReport(venueCount: number): unknown {
  const venues = buildDoohVenues(venueCount);
  const totals = doohVenueTotals(venues);

  const byType = new Map<string, number>();
  for (const v of venues) byType.set(v.venueType, (byType.get(v.venueType) ?? 0) + 1);

  return {
    dataModelVersion: 1,
    metricsVersion: 'v2',
    lifecycle: 'POSTFLIGHT',
    selection: {
      adSelection: 'delivery-in-window',
      adStatusesIncluded: ['ACTIVE'],
      adGroupSelection: 'delivery-in-window',
      creativesShownLimited: false,
      nonActiveDeliveringAds: 0,
    },
    report: { id: 'r-dooh-1', version: 1, generatedAt: '2026-05-01T00:00:00Z', periodStart: '2026-04-01', periodEnd: '2026-04-30' },
    campaign: {
      id: 'c-dooh-1', name: 'Regional DOOH Launch', reportSo: 'DOOH1', so: 'DOOH1', ios: ['IO-D1'],
      clientName: 'Client Sample Report', agencyName: 'YOUR COMPANY NAME',
      startDate: '04/01/2026', endDate: '04/30/2026', channels: ['DOOH'], bannerAssetId: 'asset-dooh-creative',
    },
    overall: {
      delivery: {
        impressions: Math.round(totals.impressions),
        retargetImpressions: null, clicks: null, retargetClicks: null,
        mobileClicks: null, desktopClicks: null,
      },
      contract: { targetImpressions: 250000 },
      // DOOH has no clicks, so no CTR. Null, not zero — a zero CTR would read as a
      // campaign that performed terribly rather than a channel without the concept.
      derived: { ctr: null, overDeliveredImpressions: null, overDeliveryPct: null },
      devices: [], browsers: [],
    },
    channels: [], adGroups: [],
    targeting: { zips: [], audience: { notes: [] }, topSites: [] },

    dooh: {
      potentialVenues: [],
      potentialVenuesRankedBy: 'audience',
      venueSummary: [...byType.entries()].map(([venueType, screens]) => ({ venueType, screens }))
        .sort((a, b) => b.screens - a.screens),
      totalPotentialVenues: venues.length,
      screenCount: venues.length,
      actualImpressions: totals.impressions,
      actualPlays: totals.plays,
      venueDelivery: venues,
      unmatchedScreens: totals.unmatched,
      coercedMultipliers: 0,
    },
    presentation: {
      showCitiesChart: false,
    },
    provenance: {
      overrideCount: 0,
      overridesByKind: { DATA_GAP: 0, CONTRACTUAL: 0, PRESENTATION: 0 },
      hasAdjustedFigures: false,
    },
  };
}

/** The default fixture: large enough to span many pages. */
export const SAMPLE_DOOH_REPORT: unknown = buildDoohReport(5000);

// ---------------------------------------------------------------------------
// Pre-flight
// ---------------------------------------------------------------------------

const STREETS = ['Main St', 'Broadway', 'Elm St', 'Washington Ave', 'Park Rd', 'Union St', 'Market St'];

/**
 * A pre-flight placement projection.
 *
 * D42 caps the visible list at the **top 100 ranked by estimated audience**, with the
 * remainder rolled up by venue type and an explicit "showing 100 of N". The cap is
 * enforced by the data model — `potentialVenues` is `.max(100)`, so a template
 * physically cannot exceed it — and this fixture exercises the case that matters: a
 * campaign matching far more inventory than it can list.
 *
 * The critical property is that `venueSummary` covers **all** matching placements, not
 * just the visible hundred. That is the completeness claim the capped table can no
 * longer make on its own; without it the report would show 100 venues and imply that
 * was the whole buy.
 */
export function buildDoohPreflightReport(totalMatching = 2847): unknown {
  const candidates = [];
  for (let i = 0; i < totalMatching; i++) {
    const vt = VENUE_TYPES[i % VENUE_TYPES.length];
    // Deterministic per-venue capacity, so ranking is stable across runs (R4).
    const capacity = 20 + ((i * 37) % 400);
    candidates.push({
      screenId: String(700000 + i),
      venueType: vt.type,
      venueName: `${vt.type.split(' ')[0]} #${2000 + i}`,
      addressLine: `${100 + ((i * 13) % 900)} ${STREETS[i % STREETS.length]}`,
      cityStateZip: CITIES[i % CITIES.length],
      estimatedAudience: Number((capacity * vt.multiplier).toFixed(1)),
    });
  }

  // Rank by estimated audience, then by screenId so ties break deterministically.
  const ranked = [...candidates].sort(
    (a, b) => b.estimatedAudience - a.estimatedAudience || a.screenId.localeCompare(b.screenId)
  );
  const top = ranked.slice(0, 100);

  // Summary spans every candidate, not just the visible hundred.
  const byType = new Map<string, number>();
  for (const c of candidates) byType.set(c.venueType, (byType.get(c.venueType) ?? 0) + 1);

  return {
    dataModelVersion: 1,
    metricsVersion: 'v2',
    lifecycle: 'PREFLIGHT',
    selection: {
      adSelection: 'delivery-in-window',
      adStatusesIncluded: [],
      adGroupSelection: 'delivery-in-window',
      // The list was trimmed for presentation; the totals below are not.
      creativesShownLimited: true,
      nonActiveDeliveringAds: 0,
    },
    report: { id: 'r-dooh-pre-1', version: 1, generatedAt: '2026-03-15T00:00:00Z', periodStart: '2026-04-01', periodEnd: '2026-04-30' },
    campaign: {
      id: 'c-dooh-pre-1', name: 'Regional DOOH Launch', reportSo: 'DOOHPRE1', so: 'DOOHPRE1', ios: ['IO-DP1'],
      clientName: 'Client Sample Report', agencyName: 'YOUR COMPANY NAME',
      startDate: '04/01/2026', endDate: '04/30/2026', channels: ['DOOH'], bannerAssetId: 'asset-dooh-creative',
    },
    overall: {
      // Pre-flight has no delivery. NULL, never 0 — a zero here would claim the
      // campaign ran and delivered nothing.
      delivery: {
        impressions: null, retargetImpressions: null, clicks: null,
        retargetClicks: null, mobileClicks: null, desktopClicks: null,
      },
      contract: { targetImpressions: 250000 },
      derived: { ctr: null, overDeliveredImpressions: null, overDeliveryPct: null },
      devices: [], browsers: [],
    },
    channels: [], adGroups: [],
    targeting: {
      zips: ['02108', '02903', '01608'],
      radiusMiles: 25,
      mapAssetId: 'asset-dooh-preflight-map',
      audience: { notes: ['Adults 25–54', 'Household income 75k+', 'Within 25 miles of a target ZIP'] },
      topSites: [],
    },
    dooh: {
      potentialVenues: top,
      potentialVenuesRankedBy: 'audience',
      venueSummary: [...byType.entries()].map(([venueType, screens]) => ({ venueType, screens }))
        .sort((a, b) => b.screens - a.screens),
      totalPotentialVenues: candidates.length,
      screenCount: candidates.length,
      // No delivery yet. These render as TBD, which is the DOOH mockup's own case.
      actualImpressions: null,
      actualPlays: null,
      venueDelivery: [],
      unmatchedScreens: 0,
      coercedMultipliers: 0,
      mapAssetId: 'asset-dooh-preflight-map',
    },
    presentation: {
      showCitiesChart: false,
    },
    provenance: {
      overrideCount: 0,
      overridesByKind: { DATA_GAP: 0, CONTRACTUAL: 0, PRESENTATION: 0 },
      hasAdjustedFigures: false,
    },
  };
}

export const SAMPLE_DOOH_PREFLIGHT_REPORT: unknown = buildDoohPreflightReport();
